import {
  Download,
  Monitor,
  Apple,
  Laptop,
  Server,
  FileCode,
  Copy,
  ExternalLink,
  CheckCircle,
  KeyRound,
  ShieldCheck,
  Rocket,
  ArrowRight,
  Zap,
} from 'lucide-react'
import { useMemo, useState } from 'react'

const launcherDownload = {
  filename: 'SimCoachesLauncher-Setup.exe',
  downloadUrl: '/downloads/SimCoachesLauncher-Setup.exe',
  available: true,
}
import toast from 'react-hot-toast'

interface DownloadOption {
  id: string
  name: string
  description: string
  platform: string
  icon: React.ElementType
  version: string
  size: string
  filename: string
  downloadUrl: string
  available: boolean
  recommendedFor?: string
}

const downloads: DownloadOption[] = [
  {
    id: 'windows-client',
    name: 'Windows Installer',
    description: 'Recommended for sim-center deployments, front desks, and permanent support endpoints.',
    platform: 'Windows 10/11',
    icon: Monitor,
    version: '1.2.3',
    size: '24.5 MB',
    filename: 'SimCoachesRemote-Setup-1.2.3.exe',
    downloadUrl: '/downloads/SimCoachesRemote-Setup-1.2.3.exe',
    available: false,
    recommendedFor: 'Primary deployment path',
  },
  {
    id: 'windows-portable',
    name: 'Windows Portable',
    description: 'Best for emergency support, validation, or one-time sessions without a full install.',
    platform: 'Windows 10/11',
    icon: FileCode,
    version: '1.2.3',
    size: '18.2 MB',
    filename: 'SimCoachesRemote-Portable-1.2.3.exe',
    downloadUrl: '/downloads/SimCoachesRemote-Portable-1.2.3.exe',
    available: false,
    recommendedFor: 'Fast fallback',
  },
  {
    id: 'macos-client',
    name: 'macOS Client',
    description: 'For internal admin Macs or customer office systems that need remote support access.',
    platform: 'macOS 11+',
    icon: Apple,
    version: '1.2.3',
    size: '32.1 MB',
    filename: 'SimCoachesRemote-1.2.3.dmg',
    downloadUrl: '/downloads/SimCoachesRemote-1.2.3.dmg',
    available: false,
  },
  {
    id: 'linux-client',
    name: 'Linux Client',
    description: 'For Linux-based endpoints, kiosks, or support infrastructure environments.',
    platform: 'Ubuntu 20.04+, Debian 11+',
    icon: Laptop,
    version: '1.2.3',
    size: '28.7 MB',
    filename: 'simcoaches-remote-1.2.3.deb',
    downloadUrl: '/downloads/simcoaches-remote-1.2.3.deb',
    available: false,
  },
]

function readClaimContext() {
  const params = new URLSearchParams(window.location.search)
  const rawToken = params.get('claimToken') || params.get('token') || params.get('enrollmentToken') || ''
  const claimUrl = params.get('claimUrl') || ''
  const orgName = params.get('org') || params.get('organization') || params.get('customer') || ''
  const installer = params.get('installer') || params.get('platform') || 'windows-client'

  return {
    claimToken: rawToken.trim(),
    claimUrl: claimUrl.trim(),
    orgName: orgName.trim(),
    installer,
  }
}

export default function DownloadsPage() {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [copiedValue, setCopiedValue] = useState<string | null>(null)
  const claimContext = useMemo(readClaimContext, [])

  const recommendedDownload =
    downloads.find((download) => download.id === claimContext.installer) || downloads[0]

  const copyValue = async (value: string, label: string, key: string) => {
    await navigator.clipboard.writeText(value)
    setCopiedValue(key)
    toast.success(`${label} copied`)
    window.setTimeout(() => setCopiedValue(null), 2000)
  }

  const copyDownloadLink = async (download: DownloadOption) => {
    const fullUrl = `${window.location.origin}${download.downloadUrl}`
    await navigator.clipboard.writeText(fullUrl)
    setCopiedId(download.id)
    toast.success('Download link copied')
    window.setTimeout(() => setCopiedId(null), 2000)
  }

  const handleDownloadClick = (download: DownloadOption) => {
    if (!download.available) {
      toast.error('Upload the signed installer into web/public/downloads first')
      return
    }

    if (claimContext.claimToken) {
      toast.success('Installer download started — use the claim token during onboarding')
    }
  }

  const onboardingSteps = [
    {
      title: 'Download the Windows installer',
      description:
        'Use the standard installer for permanent sim-center deployment. Keep the portable build for troubleshooting or rapid validation.',
    },
    {
      title: 'Run installer as the on-site operator',
      description:
        claimContext.claimToken
          ? 'During onboarding, paste the claim token below so the PC attaches directly to the right customer account.'
          : 'If you created an enrollment token from Devices, paste it during onboarding to attach the PC to the correct customer account.',
    },
    {
      title: 'Verify claim and relay pairing',
      description:
        'Confirm the device appears in PitStop under the expected customer, shows a claimed state, and reports its RustDesk ID for technician launch.',
    },
    {
      title: 'Hand off to support or operations',
      description:
        'Technicians can launch support from the Devices page, while the customer keeps a clean Sim Coaches-branded support path for future incidents.',
    },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sc-text-primary">Downloads & Onboarding</h1>
          <p className="text-sc-text-muted mt-1 max-w-3xl">
            Sim Coaches deployment tools for customer installs, internal support handoff, and account-linked device onboarding.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="badge bg-sc-primary/10 text-sc-primary">
            <ShieldCheck className="w-3.5 h-3.5" />
            Windows-first deployment flow
          </span>
          <span className="badge bg-sc-bg-light text-sc-text-secondary">
            v{recommendedDownload.version}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="card border-sc-primary/20 bg-gradient-to-br from-sc-primary/10 via-sc-bg-dark to-sc-bg-dark">
          <div className="card-body space-y-5">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-sc-primary/15 border border-sc-primary/20">
                <Rocket className="w-6 h-6 text-sc-primary" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-semibold text-sc-text-primary">Recommended deployment path</h2>
                  <span className="badge bg-sc-primary/15 text-sc-primary">Sim Coaches standard</span>
                </div>
                <p className="text-sm text-sc-text-secondary mt-2 max-w-2xl">
                  Start with the Windows installer for permanent sim-center devices. It gives ops and support a cleaner enrollment flow, stronger branding continuity, and a clearer handoff into PitStop device management.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 p-4 rounded-xl bg-sc-bg-dark/70 border border-sc-border-dark">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-sc-text-muted">Primary package</p>
                <h3 className="mt-2 text-lg font-semibold text-sc-text-primary">{recommendedDownload.name}</h3>
                <p className="text-sm text-sc-text-secondary mt-1">{recommendedDownload.description}</p>
                <div className="flex flex-wrap gap-2 mt-3 text-xs text-sc-text-muted">
                  <span>{recommendedDownload.platform}</span>
                  <span>•</span>
                  <span>{recommendedDownload.size}</span>
                  <span>•</span>
                  <span>{recommendedDownload.filename}</span>
                </div>
              </div>
              <div className="flex md:flex-col gap-3 md:items-end">
                <a
                  href={recommendedDownload.downloadUrl}
                  download
                  onClick={(event) => {
                    if (!recommendedDownload.available) {
                      event.preventDefault()
                      handleDownloadClick(recommendedDownload)
                      return
                    }
                    handleDownloadClick(recommendedDownload)
                  }}
                  className="btn-primary"
                  aria-disabled={!recommendedDownload.available}
                >
                  <Download className="w-4 h-4" />
                  {recommendedDownload.available ? 'Download installer' : 'Pending upload'}
                </a>
                <button
                  onClick={() => copyDownloadLink(recommendedDownload)}
                  className="btn-secondary"
                >
                  {copiedId === recommendedDownload.id ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-sc-accent-green" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy link
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="p-4 rounded-xl bg-sc-bg-medium/40 border border-sc-border-dark">
                <p className="text-sc-text-muted">Install motion</p>
                <p className="mt-1 font-medium text-sc-text-primary">Download → install → claim → verify</p>
              </div>
              <div className="p-4 rounded-xl bg-sc-bg-medium/40 border border-sc-border-dark">
                <p className="text-sc-text-muted">Best for</p>
                <p className="mt-1 font-medium text-sc-text-primary">Sim rigs, reception PCs, training stations</p>
              </div>
              <div className="p-4 rounded-xl bg-sc-bg-medium/40 border border-sc-border-dark">
                <p className="text-sc-text-muted">Relay target</p>
                <p className="mt-1 font-medium text-sc-text-primary">remote.simcoaches.com</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-sc-accent-cyan/20">
          <div className="card-body space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-sc-accent-cyan/10">
                <KeyRound className="w-5 h-5 text-sc-accent-cyan" />
              </div>
              <div>
                <h2 className="font-semibold text-sc-text-primary">Claim-aware onboarding</h2>
                <p className="text-sm text-sc-text-secondary mt-1">
                  If support created a customer-specific token, use it during install so the machine lands in the correct account on first boot.
                </p>
              </div>
            </div>

            {claimContext.claimToken || claimContext.claimUrl || claimContext.orgName ? (
              <div className="space-y-3 p-4 rounded-xl border border-sc-accent-cyan/30 bg-sc-accent-cyan/5">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sc-text-muted">Detected deployment context</p>
                  <p className="mt-2 text-sm text-sc-text-primary font-medium">
                    {claimContext.orgName || 'Customer-linked install ready'}
                  </p>
                </div>

                {claimContext.claimToken && (
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="text-sm text-sc-text-secondary">Claim token</p>
                      <button
                        onClick={() => copyValue(claimContext.claimToken, 'Claim token', 'claim-token')}
                        className="btn-secondary py-1.5 px-3 text-xs"
                      >
                        {copiedValue === 'claim-token' ? (
                          <>
                            <CheckCircle className="w-3.5 h-3.5 text-sc-accent-green" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy token
                          </>
                        )}
                      </button>
                    </div>
                    <code className="block p-3 rounded-lg bg-sc-bg-dark text-sc-text-primary break-all text-sm">
                      {claimContext.claimToken}
                    </code>
                  </div>
                )}

                {claimContext.claimUrl && (
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => copyValue(claimContext.claimUrl, 'Claim URL', 'claim-url')}
                      className="btn-secondary py-1.5 px-3 text-xs"
                    >
                      {copiedValue === 'claim-url' ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-sc-accent-green" />
                          Copied
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-3.5 h-3.5" />
                          Copy claim URL
                        </>
                      )}
                    </button>
                    <a href={claimContext.claimUrl} className="btn-primary py-1.5 px-3 text-xs">
                      <ArrowRight className="w-3.5 h-3.5" />
                      Open claim flow
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-sc-bg-medium/30 border border-sc-border-dark text-sm text-sc-text-muted">
                No claim token detected in this URL. If you came from Devices, append the customer claim link or token here to guide field installs.
              </div>
            )}

            <div className="p-4 rounded-xl bg-sc-primary/5 border border-sc-primary/20 text-sm text-sc-text-secondary">
              <p className="font-medium text-sc-text-primary mb-1">Internal support tip</p>
              Create the enrollment token in <span className="text-sc-primary">Devices</span>, then send this downloads page with token/query parameters to on-site staff. It turns a generic download page into a customer-linked install handoff.
            </div>
          </div>
        </div>
      </div>

      <div className="card bg-sc-primary/5 border-sc-primary/20">
        <div className="card-body py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-sc-primary/10">
              <Server className="w-5 h-5 text-sc-primary" />
            </div>
            <div>
              <h3 className="font-medium text-sc-text-primary">Sim Coaches-hosted relay posture</h3>
              <p className="text-sm text-sc-text-secondary mt-1">
                Device connectivity is designed around a branded Sim Coaches support path while still preserving your controlled deployment flow. Clients connect to{' '}
                <code className="px-1.5 py-0.5 bg-sc-bg-dark rounded text-sc-primary">
                  remote.simcoaches.com
                </code>{' '}
                and can be claimed into the correct customer account during onboarding.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {downloads.map((download) => (
          <div key={download.id} className="card hover:border-sc-border-medium transition-colors">
            <div className="card-body">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-sc-bg-light">
                  <download.icon className="w-6 h-6 text-sc-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-sc-text-primary">{download.name}</h3>
                        {download.recommendedFor && (
                          <span className="badge bg-sc-primary/10 text-sc-primary">
                            {download.recommendedFor}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-sc-text-muted mt-0.5">{download.platform}</p>
                    </div>
                    <span className="badge bg-sc-bg-light text-sc-text-secondary">v{download.version}</span>
                  </div>

                  <p className="text-sm text-sc-text-secondary mt-3">{download.description}</p>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-sc-border-dark flex-wrap">
                    <div className="text-xs text-sc-text-muted">
                      <span className="font-medium">{download.size}</span>
                    </div>
                    <div className="text-xs text-sc-text-muted">
                      {download.available ? 'Ready for deployment' : 'Artifact missing'}
                    </div>
                    <div className="flex-1" />
                    <button
                      onClick={() => copyDownloadLink(download)}
                      className="btn-ghost py-1.5 px-3 text-xs"
                    >
                      {copiedId === download.id ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-sc-accent-green" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          Copy Link
                        </>
                      )}
                    </button>
                    <a
                      href={download.downloadUrl}
                      className="btn-primary py-1.5 px-3 text-xs"
                      download
                      aria-disabled={!download.available}
                      onClick={(event) => {
                        if (!download.available) {
                          event.preventDefault()
                          handleDownloadClick(download)
                          return
                        }
                        handleDownloadClick(download)
                      }}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {download.available ? 'Download' : 'Pending Upload'}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-sc-text-primary">Windows-first deployment checklist</h2>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {onboardingSteps.map((step, index) => (
              <div key={step.title} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sc-primary/10 flex items-center justify-center text-sc-primary font-medium text-sm flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-medium text-sc-text-primary">{step.title}</h4>
                  <p className="text-sm text-sc-text-muted mt-1">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technician Launcher section */}
      <div className="card border-sc-accent-yellow/20 bg-gradient-to-br from-sc-accent-yellow/5 via-sc-bg-dark to-sc-bg-dark">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sc-accent-yellow/10">
              <Zap className="w-5 h-5 text-sc-accent-yellow" />
            </div>
            <div>
              <h2 className="font-semibold text-sc-text-primary">Technician Launcher</h2>
              <p className="text-sm text-sc-text-muted mt-0.5">
                Install once on each technician machine — enables one-click session launch from this dashboard
              </p>
            </div>
          </div>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-sc-text-secondary">
            The Technician Launcher handles the{' '}
            <code className="px-1.5 py-0.5 bg-sc-bg-dark rounded text-sc-primary">simcoaches://</code>{' '}
            URL scheme. When you click <strong className="text-sc-text-primary">Connect</strong> on a device,
            the dashboard opens this URL, the launcher consumes the one-time ticket, and RustDesk launches
            directly into the session — no copy-pasting host IDs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="p-3 rounded-xl bg-sc-bg-medium/50 border border-sc-border-dark">
              <p className="font-medium text-sc-text-primary mb-1">1. Install once</p>
              <p className="text-sc-text-muted">
                Run the installer on every technician's machine. Takes under a minute.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-sc-bg-medium/50 border border-sc-border-dark">
              <p className="font-medium text-sc-text-primary mb-1">2. Register URL scheme</p>
              <p className="text-sc-text-muted">
                The installer registers <code className="text-sc-primary">simcoaches://</code> as an OS-level handler.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-sc-bg-medium/50 border border-sc-border-dark">
              <p className="font-medium text-sc-text-primary mb-1">3. Click Connect</p>
              <p className="text-sc-text-muted">
                RustDesk opens automatically, already pointed at the right sim rig and relay.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href={launcherDownload.downloadUrl}
              className="btn-primary"
              download={launcherDownload.filename}
              onClick={() => toast.success('Launcher download started')}
            >
              <Download className="w-4 h-4" />
              Download Launcher (Windows)
            </a>
            <a
              href="https://github.com/simcoaches/pitstop/releases"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <ExternalLink className="w-4 h-4" />
              Source + All Platforms
            </a>
          </div>

          <div className="p-3 rounded-xl bg-sc-bg-medium/30 border border-sc-border-dark text-xs text-sc-text-muted font-mono">
            <p className="text-sc-text-secondary mb-1">Quick install (Node.js):</p>
            <p>cd launcher &amp;&amp; npm install &amp;&amp; node src/index.js --setup &amp;&amp; node src/index.js --register</p>
          </div>
        </div>
      </div>

      <div className="card border-sc-accent-cyan/20">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-sc-accent-cyan/10">
              <ExternalLink className="w-5 h-5 text-sc-accent-cyan" />
            </div>
            <div>
              <h3 className="font-medium text-sc-text-primary">Enterprise deployment</h3>
              <p className="text-sm text-sc-text-secondary mt-1">
                For multi-PC rollouts, publish the signed MSI into <code className="px-1.5 py-0.5 bg-sc-bg-dark rounded text-sc-primary">web/public/downloads</code> and use this portal as the official Sim Coaches distribution endpoint for scripted or silent installs.
              </p>
              <button
                className="btn-secondary mt-4 py-1.5 text-sm"
                onClick={() => toast.error('MSI package is not published yet — use the launcher EXE for now.')}
              >
                <Download className="w-4 h-4" />
                Download MSI Package
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
